import React, {
  createContext,
  useCallback,
  useEffect,
  useState,
  useMemo,
} from 'react';
import { KiteProgressIndicator } from '@kite/react-kite';
import { IAuthSessionContext } from './types/authSession';
import authContextDefault from './utils/authSessionDefault';
import { useUserIdle } from './hooks/useUserIdle';
import {
  IAuthSessionSignOutProps,
  useAccessToken,
} from './hooks/useAccessToken';

/**
 * The Auth Session context serves as an abstraction layer built on top of the aforementioned hooks.
 * It facilitates App Specific business logic and enables component interaction with Auth Sessions.
 * @See https://chalk.charter.com/display/ERSPM/FE+Auth+State+Pattern
 */
export const AuthSessionContext =
  createContext<IAuthSessionContext>(authContextDefault);

/**
 * Handles the management of a Auth Session
 */
export const AuthSessionContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const {
    isLoading: isAccessTokenLoading,
    accessToken,
    idToken,
    idTokenObj,
    legacyToken,
    isAuthenticating,
    isAuthenticated,
    isAuthError,
    signOut: accessTokenSignOut,
    refreshTokenIfExpired,
  } = useAccessToken();

  useEffect(() => {
    // Show Full Page Loading until everything is done loading
    setIsLoading(isAccessTokenLoading);
  }, [isAccessTokenLoading]);

  /** Called anytime a user is interacting with the site  */
  const handleOnUserInteraction = useCallback(() => {
    // if the access token hasn't been updated in awhile and user is still active, refresh token.
    refreshTokenIfExpired().catch(console.error);
  }, [refreshTokenIfExpired]);

  /**
   * Monitors user inactivity
   */
  const { showIdlePrompt, resetIdlePrompt, isUserIdleLogout } = useUserIdle({
    isAuthenticated,
    onUserInteraction: handleOnUserInteraction,
  });

  /**
   * Sign the user out and clear auth session
   * @param showError whether or not to show the Error Message banner on logout
   */
  const signOut = useCallback(
    async (options?: IAuthSessionSignOutProps) => {
      await accessTokenSignOut(options);
    },
    [accessTokenSignOut]
  );

  useEffect(() => {
    if (isUserIdleLogout) {
      // The user has been inactive for too long, its time to automatically sign them out.
      resetIdlePrompt();
      signOut().catch(console.error);
    }
  }, [isUserIdleLogout, signOut]);

  return (
    <AuthSessionContext.Provider
      value={useMemo(
        () => ({
          isAuthenticating,
          isAuthenticated,
          isAuthError,
          authToken: accessToken,
          idToken,
          idTokenObj,
          legacyToken,
          isUserIdleLogout,
          showIdlePrompt,
          resetIdlePrompt,
          signOut,
        }),
        [
          isAuthenticating,
          isAuthenticated,
          isAuthError,
          accessToken,
          idToken,
          idTokenObj,
          legacyToken,
          isUserIdleLogout,
          showIdlePrompt,
          resetIdlePrompt,
          signOut,
        ]
      )}
    >
      {!isLoading && children}
      {isLoading && (
        <div className="display-loader">
          <KiteProgressIndicator id="auth-session-loader" />
        </div>
      )}
    </AuthSessionContext.Provider>
  );
};
